import type { ReportBasis } from './api/reports';
import { ExtractRuleCode } from './api/useMedia';
import { t } from './i18n';

/**
 * HTTP_STATUS: HTTP response status codes used to handle API responses in the SPA.
 * Single source of truth — import from here instead of using inline numbers.
 */
export const HTTP_STATUS = {
  OK: 200,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
} as const;

/**
 * HTML document template for wrapping mail template content.
 * HTML editor content is inserted in place of {{BODY}}.
 */
export const HTML_DOCUMENT_TEMPLATE = `<!doctype html>
<html lang="ja">
<head><meta http-equiv="Content-Type" content="text/html; charset=UTF-8" /></head>
<body>{{BODY}}</body>
</html>`;

/**
 * EDITOR_HEADING_LEVELS: Heading levels enabled in the mail template rich text editor.
 * Used both to configure the editor extension and to render the heading toolbar buttons.
 */
export const EDITOR_HEADING_LEVELS = [1, 2, 3] as const;

/**
 * RESPONSE_CODES: Machine-readable code strings included in API error response bodies.
 * Mirrors the server-side RESPONSE_CODES in /workspace/src/constants/messages.ts.
 */
export const RESPONSE_CODES = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  DUPLICATE_ACCOUNT_ALIAS: 'DUPLICATE_ACCOUNT_ALIAS',
  DUPLICATE_TEMPLATE_NAME: 'DUPLICATE_TEMPLATE_NAME',
  TEMPLATE_IN_USE: 'TEMPLATE_IN_USE',
} as const;

/**
 * RESPONSE_DETAIL_FIELDS: keys the backend may send inside an error response's
 * `details` field — structured, non-prose data (never server-composed
 * sentences) that the SPA interpolates into a localised message.
 *
 * Single source of truth — `ApiErrorDetails` is derived from this, so a field
 * is named here once rather than repeated in the interface.
 *
 * Mirrors RESPONSE_DETAIL_FIELDS in atss-api/src/constants/const.ts, which
 * enforces the same allowlist server-side. Both repos must agree because these
 * are wire-protocol keys; the two lists cannot be shared as code.
 */
export const RESPONSE_DETAIL_FIELDS = {
  /** Names of filtering conditions blocking a template deletion. */
  FILTERING_CONDITION_NAMES: 'filteringConditionNames',
} as const;

export const JobNumberExtractRuleMaster = [
  { code: ExtractRuleCode.UseAsIs, labelKey: 'extract_rule_1' as const },
  { code: ExtractRuleCode.AfterHalfWidthSlash, labelKey: 'extract_rule_2' as const },
  { code: ExtractRuleCode.BeforeHalfWidthHyphen, labelKey: 'extract_rule_3' as const },
  { code: ExtractRuleCode.AfterHalfWidthHyphen, labelKey: 'extract_rule_5' as const },
  { code: ExtractRuleCode.AfterFullWidthSlash, labelKey: 'extract_rule_4' as const },
] as const;

/** Extends JobNumberExtractRuleMaster (SSOT) with rules only valid for branch number extraction. */
export const BranchNumberExtractRuleMaster = [
  ...JobNumberExtractRuleMaster.filter((rule) => rule.code !== ExtractRuleCode.UseAsIs),
  { code: ExtractRuleCode.LastOneDigit, labelKey: 'extract_rule_6' as const },
  { code: ExtractRuleCode.LastTwoDigits, labelKey: 'extract_rule_7' as const },
  { code: ExtractRuleCode.LastThreeDigits, labelKey: 'extract_rule_8' as const },
] as const;

/**
 * Forbidden combinations of extractRule and extractBranchNumberRule.
 * Defines which pairs of extraction rules cannot be used together.
 */
export const FORBIDDEN_EXTRACT_RULE_COMBINATIONS = [
  {
    extractRule: ExtractRuleCode.AfterHalfWidthSlash,
    extractBranchNumberRule: ExtractRuleCode.BeforeHalfWidthHyphen,
  },
  {
    extractRule: ExtractRuleCode.BeforeHalfWidthHyphen,
    extractBranchNumberRule: ExtractRuleCode.AfterHalfWidthHyphen,
  },
  {
    extractRule: ExtractRuleCode.AfterHalfWidthHyphen,
    extractBranchNumberRule: ExtractRuleCode.BeforeHalfWidthHyphen,
  },
] as const;

export const ACCOUNT_ALIAS_MAX_LENGTH = 64;

/**
 * Maximum length for template name fields (mail_template.name, sms_template_name.name).
 */
export const TEMPLATE_NAME_MAX_LENGTH = 64;

/**
 * Maximum length for mail template subject (mail_template_contents.subject).
 */
export const TEMPLATE_SUBJECT_MAX_LENGTH = 128;

/**
 * Maximum character count for SMS template body.
 */
export const SMS_BODY_MAX_LENGTH = 160;

/**
 * TemplateType: Discriminates between mail and SMS templates.
 * Mirrors the backend TemplateType constant.
 */
export const TemplateType = {
  MAIL: 1,
  SMS: 2,
} as const;
export type TemplateType = (typeof TemplateType)[keyof typeof TemplateType];

/**
 * MailTemplateType: Discriminates the audience for mail templates.
 * 1 = TO_COMPANY (社内通知), 2 = TO_APPLICANT (応募者通知)
 */
export const MailTemplateType = {
  TO_COMPANY: 1,
  TO_APPLICANT: 2,
} as const;
export type MailTemplateType = (typeof MailTemplateType)[keyof typeof MailTemplateType];

/**
 * MAIL_TEMPLATE_TAGS: Master data for mail template tags.
 * The id is stored in mail_template.tags as a comma-separated string.
 */
export const MAIL_TEMPLATE_TAGS = [
  { id: 1, labelKey: 'template_tag_1' as const },
  { id: 2, labelKey: 'template_tag_2' as const },
  { id: 3, labelKey: 'template_tag_3' as const },
] as const;
export type MailTemplateTagId = (typeof MAIL_TEMPLATE_TAGS)[number]['id'];

/** Set of valid tag IDs for fast lookup. */
export const VALID_MAIL_TEMPLATE_TAG_IDS: ReadonlySet<number> = new Set(
  MAIL_TEMPLATE_TAGS.map((tag) => tag.id),
);

/** Variable placeholders available in all template types (mail and SMS). */
export const TEMPLATE_VARIABLES_COMMON = [
  { label: t('template_var_form_url'), placeholder: '{{formUrl}}' },
  { label: t('template_var_form_url_file'), placeholder: '{{formUrlFile}}' },
  { label: t('template_var_zloss_url'), placeholder: '{{zlossUrl}}' },
  { label: t('template_var_applicant_name'), placeholder: '{{applicant.name}}' },
  { label: t('template_var_applicant_job_number'), placeholder: '{{applicant.jobNumber}}' },
  { label: t('template_var_applicant_job_title'), placeholder: '{{applicant.jobTitle}}' },
];

/** Additional variable placeholders only available in MAIL TO_COMPANY (社内通知) templates. */
export const TEMPLATE_VARIABLES_TO_COMPANY = [
  { label: t('template_var_resume_id'), placeholder: '{{resumeId}}' },
  { label: t('template_var_resume_url'), placeholder: '{{resumeUrl}}' },
  { label: t('template_var_system_account_name'), placeholder: '{{system.accountName}}' },
];

/**
 * Regular expression that matches positive integer numbers (1, 2, 3...).
 * Used for client-side validation of fixed job numbers.
 */
export const POSITIVE_INTEGER_REGEX = /^[1-9]\d*$/;

export const SNACKBAR_AUTO_HIDE_DURATION = 3000;

export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

export const KEYWORD_DEBOUNCE_MS = 300;

export const DASHBOARD_REPORT_MAX_RANGE_DAYS = 183;
// Earliest selectable date (inclusive). Month is 0-indexed: 9 = October.
export const DASHBOARD_REPORT_MIN_SELECTABLE_DATE = new Date(2026, 9, 15);

export const DEFAULT_REPORT_BASIS: ReportBasis = 'entry';
export const HELP_URL_MEDIA_ACQUISITION_METHOD =
  'https://hrbc-support.porters.jp/hc/ja/articles/45871367010969-SAISOKU-%E5%AF%BE%E5%BF%9C%E3%81%97%E3%81%A6%E3%81%84%E3%82%8B%E6%B1%82%E4%BA%BA%E5%AA%92%E4%BD%93%E4%B8%80%E8%A6%A7';

export type SortField = 'accountAlias' | 'mediaName' | 'linkStatus' | 'mediaType';
export type SortOrder = 'asc' | 'desc';

export {
  UseMediaAccountLinkStatus,
  JobNumberAcquisitionType,
  RegisterChannelUse,
  ExtractRuleCode,
} from './api/useMedia';
export { MediaType, JobNumberCustomizePattern, LoginConfirmStatus } from './api/media';
export { InformationCategory } from './api/informations';
